import React from 'react';
import { Box, Fade, Slide, Zoom, Grow, Collapse, SxProps, Theme } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

/**
 * Enhanced Animation Components
 * Provides smooth, accessible animations for dashboard elements
 */

interface AnimationProps {
  children: React.ReactNode;
  direction?: 'up' | 'down' | 'left' | 'right';
  delay?: number;
  duration?: number;
  in?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Fade In Animation with customizable delay and duration
 */
export const FadeInAnimation: React.FC<AnimationProps> = ({
  children,
  delay = 0,
  duration = 500,
  in: inProp = true,
  sx = {}
}) => {
  const [show, setShow] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setShow(inProp), delay);
    return () => clearTimeout(timer);
  }, [delay, inProp]);

  return (
    <Fade
      in={show}
      timeout={duration}
      style={{ transitionDelay: show ? `${delay}ms` : '0ms' }}
    >
      <Box sx={sx}>
        {children}
      </Box>
    </Fade>
  );
};

/**
 * Slide Animation with directional support
 */
export const SlideAnimation: React.FC<AnimationProps> = ({
  children,
  direction = 'up',
  delay = 0,
  duration = 400,
  in: inProp = true,
  sx = {}
}) => {
  const [show, setShow] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setShow(inProp), delay);
    return () => clearTimeout(timer);
  }, [delay, inProp]);

  return (
    <Slide
      direction={direction}
      in={show}
      timeout={duration}
      style={{ transitionDelay: show ? `${delay}ms` : '0ms' }}
    >
      <Box sx={sx}>
        {children}
      </Box>
    </Slide>
  );
};

/**
 * Zoom Animation for attention-grabbing elements
 */
export const ZoomAnimation: React.FC<AnimationProps> = ({
  children,
  delay = 0,
  duration = 300,
  in: inProp = true,
  sx = {}
}) => {
  const [show, setShow] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setShow(inProp), delay);
    return () => clearTimeout(timer);
  }, [delay, inProp]);

  return (
    <Zoom
      in={show}
      timeout={duration}
      style={{ transitionDelay: show ? `${delay}ms` : '0ms' }}
    >
      <Box sx={sx}>
        {children}
      </Box>
    </Zoom>
  );
};

/**
 * Grow Animation for expanding elements
 */
export const GrowAnimation: React.FC<AnimationProps> = ({
  children,
  delay = 0,
  duration = 350,
  in: inProp = true,
  sx = {}
}) => {
  const [show, setShow] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setShow(inProp), delay);
    return () => clearTimeout(timer);
  }, [delay, inProp]);

  return (
    <Grow
      in={show}
      timeout={duration}
      style={{ transitionDelay: show ? `${delay}ms` : '0ms' }}
    >
      <Box sx={sx}>
        {children}
      </Box>
    </Grow>
  );
};

/**
 * Staggered Animation Container
 * Animates children with sequential delays
 */
interface StaggeredAnimationProps {
  children: React.ReactNode[];
  animation?: 'fade' | 'slide' | 'zoom' | 'grow';
  direction?: 'up' | 'down' | 'left' | 'right';
  staggerDelay?: number;
  initialDelay?: number;
  duration?: number;
  sx?: SxProps<Theme>;
}

export const StaggeredAnimation: React.FC<StaggeredAnimationProps> = ({
  children,
  animation = 'fade',
  direction = 'up',
  staggerDelay = 100,
  initialDelay = 0,
  duration = 400,
  sx = {}
}) => {
  const AnimationComponent = React.useMemo(() => {
    switch (animation) {
      case 'slide': return SlideAnimation;
      case 'zoom': return ZoomAnimation;
      case 'grow': return GrowAnimation;
      default: return FadeInAnimation;
    }
  }, [animation]);

  return (
    <Box sx={sx}>
      {React.Children.map(children, (child, index) => (
        <AnimationComponent
          key={index}
          delay={initialDelay + (index * staggerDelay)}
          duration={duration}
          direction={direction}
        >
          {child}
        </AnimationComponent>
      ))}
    </Box>
  );
};

/**
 * Page Transition Animation
 * For smooth page/route transitions
 */
interface PageTransitionProps {
  children: React.ReactNode;
  isLoading?: boolean;
  transitionKey?: string;
}

export const PageTransition: React.FC<PageTransitionProps> = ({
  children,
  isLoading = false,
  transitionKey
}) => {
  return (
    <Box
      key={transitionKey}
      sx={{
        minHeight: '100vh',
        transition: 'all 0.3s ease-in-out',
        opacity: isLoading ? 0.7 : 1,
        transform: isLoading ? 'scale(0.98)' : 'scale(1)',
      }}
    >
      <FadeInAnimation duration={600}>
        <SlideAnimation direction="up" delay={100} duration={500}>
          {children}
        </SlideAnimation>
      </FadeInAnimation>
    </Box>
  );
};

/**
 * Card Hover Animation
 * Enhanced hover effects for cards
 */
interface AnimatedCardProps {
  children: React.ReactNode;
  elevation?: number;
  hoverElevation?: number;
  sx?: SxProps<Theme>;
  onClick?: () => void;
}

export const AnimatedCard: React.FC<AnimatedCardProps> = ({
  children,
  elevation = 1,
  hoverElevation = 8,
  sx = {},
  onClick
}) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        borderRadius: 2,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: 'translateY(0px)',
        boxShadow: `0px ${elevation * 2}px ${elevation * 4}px rgba(0,0,0,0.1)`,
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0px ${hoverElevation * 2}px ${hoverElevation * 4}px rgba(0,0,0,0.15)`,
        },
        '&:active': {
          transform: 'translateY(-2px)',
        },
        ...sx
      }}
    >
      {children}
    </Box>
  );
};

/**
 * Loading Pulse Animation
 */
export const PulseAnimation: React.FC<{ children: React.ReactNode; sx?: SxProps<Theme> }> = ({
  children,
  sx = {}
}) => {
  return (
    <Box
      sx={{
        animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
        '@keyframes pulse': {
          '0%, 100%': {
            opacity: 1,
          },
          '50%': {
            opacity: 0.5,
          },
        },
        ...sx
      }}
    >
      {children}
    </Box>
  );
};

/**
 * Bounce Animation for notifications
 */
export const BounceAnimation: React.FC<{ children: React.ReactNode; sx?: SxProps<Theme> }> = ({
  children,
  sx = {}
}) => {
  return (
    <Box
      sx={{
        animation: 'bounce 1s ease-in-out',
        '@keyframes bounce': {
          '0%, 20%, 53%, 80%, 100%': {
            animationTimingFunction: 'cubic-bezier(0.215, 0.61, 0.355, 1)',
            transform: 'translate3d(0, 0, 0)',
          },
          '40%, 43%': {
            animationTimingFunction: 'cubic-bezier(0.755, 0.05, 0.855, 0.06)',
            transform: 'translate3d(0, -30px, 0) scaleY(1.1)',
          },
          '70%': {
            animationTimingFunction: 'cubic-bezier(0.755, 0.05, 0.855, 0.06)',
            transform: 'translate3d(0, -15px, 0) scaleY(1.05)',
          },
          '90%': {
            transform: 'translate3d(0, -4px, 0) scaleY(0.95)',
          },
        },
        ...sx
      }}
    >
      {children}
    </Box>
  );
};

/**
 * Shimmer Loading Animation
 */
export const ShimmerAnimation: React.FC<{
  width?: string | number;
  height?: string | number;
  sx?: SxProps<Theme>;
}> = ({
  width = '100%',
  height = 20,
  sx = {}
}) => {
  return (
    <Box
      sx={{
        width,
        height,
        background: 'linear-gradient(90deg, #f0f0f0 25%, transparent 37%, #f0f0f0 63%)',
        backgroundSize: '400% 100%',
        animation: 'shimmer 1.5s ease-in-out infinite',
        borderRadius: 1,
        '@keyframes shimmer': {
          '0%': {
            backgroundPosition: '100% 50%',
          },
          '100%': {
            backgroundPosition: '0% 50%',
          },
        },
        ...sx
      }}
    />
  );
};